import React from 'react';
import { useFilters } from '@/hooks';
import { Options } from '@/components/ui/combobox';
import { SegmentationFilterListData } from '@/interfaces';
import {
	GET_LAWYER_DIRECTORY_SEGMENT_FILTER,
	lawyerDirectoryService,
} from '@/api';
import { useInfiniteQuery } from '@tanstack/react-query';
import Icons from '@/const/icons';
import { getSegmentClientColor } from '@/lib';
import { HeaderFilterTable, Icon } from '@/components/common';

export const SegmentFilter = () => {
	const { setParamFilter, hasFilter } = useFilters();
	const observer = React.useRef<IntersectionObserver | null>(null);
	const [data, setData] = React.useState<Options[]>([]);

	const fetchData = async ({
		pageParam,
	}: {
		pageParam: number;
	}): Promise<SegmentationFilterListData> => {
		const response = await lawyerDirectoryService.getSegmentFilters(pageParam);
		return response.data;
	};

	const {
		data: segmentationList,
		fetchNextPage,
		hasNextPage,
		isFetchingNextPage,
		// isError,
		// isLoading,
	} = useInfiniteQuery({
		initialData: undefined,
		initialPageParam: 1,
		queryKey: [GET_LAWYER_DIRECTORY_SEGMENT_FILTER],
		queryFn: fetchData,
		getNextPageParam(lastPage) {
			return lastPage.page < lastPage.totalPages
				? lastPage.page + 1
				: undefined;
		},
	});

	const lastItemCallback = React.useCallback(
		(node: HTMLDivElement | null) => {
			if (isFetchingNextPage) return;
			if (observer.current) observer.current.disconnect();
			observer.current = new IntersectionObserver((entries) => {
				if (entries[0].isIntersecting && hasNextPage) {
					fetchNextPage();
				}
			});
			if (node) observer.current.observe(node);
		},
		[isFetchingNextPage, hasNextPage]
	);

	React.useEffect(() => {
		if (segmentationList) {
			const auxList: Options[] = [];
			const listFlatMapped =
				segmentationList?.pages.flatMap((page) => page.items) || [];
			listFlatMapped.map((item) =>
				auxList.push({
					label: item.name,
					value: item.id.toString(),
					icon: (
						<Icon
							src={Icons.profile}
							width={18}
							height={18}
							className={`flex-shrink-0 !text-[${getSegmentClientColor(
								item.name
							)}]`}
						/>
					),
				})
			);
			setData(auxList);
		}
	}, [segmentationList]);

	const handleSelectFilters = (value?: string) => {
		setParamFilter('segmentId', value);
	};

	const handleClearAll = () => {
		setParamFilter('segmentId', '');
	};

	return (
		<HeaderFilterTable
			title="Segment"
			searchTitle="Segment"
			filterOptions={data}
			containerRef={lastItemCallback}
			handleClearAll={handleClearAll}
			handleSelectFilters={handleSelectFilters}
			hasFilter={hasFilter('segmentId')}
			classNamePopoverContent="lg:w-[302px]"
		/>
	);
};
